import { CertificateType } from '@/enums/Charger/Certificate/CertificateType'
import { ChargerAvailabilityStatus } from '@/enums/ChargerAvailabilityStatus'
import { ChargingType, ChargingTypeLabel } from '@/enums/ChargingType'
import { ConnectorDynamicsCpmStatus } from '@/enums/Connector/ConnectorDynamicsCpmStatus'
import { RegistrationStatus } from '@/enums/RegistrationStatus'
import type { ButtonSeverity } from '@/misc/ButtonSeverity'
import type { IPaginatedResponse } from '@/misc/IPaginatedResponse'
import type { IConnector } from '@/models/Chargers/Connector/IConnector'
import type { IUpdateConnectorStaticStatus } from '@/models/Chargers/Connector/IUpdateConnectorStaticStatus'
import type { IChargerManuallyFaulted } from '@/models/Chargers/IChargerManuallyFaulted'
import type { IConnectorManuallyFaulted } from '@/models/Connectors/IConnectorManuallyFaulted'
import type { IFlatConnector } from '@/models/Connectors/IFlatConnector'
import type { FilterCondition } from '@/models/Filtering/FilterCondition'
import type { ICharger } from '@/models/ICharger'
import type { IChargingSite } from '@/models/IChargingSite'
import type { ILocalController } from '@/models/ILocalController'
import type { IStateItemCharger } from '@/models/StateItem/IStateItemCharger'
import i18n from '@/plugins/I18n'
import type { DataTableFilterMetaData } from 'primevue/datatable'
import { getIcon } from './AssetHelper'
import { convertChargingTypeToLabel } from './ChargingTypeHelper'
import { getFormattedDuration } from './DateFormatter'
import { extractValues } from './FilteringHelper'
import { getConnectorStatusLabel } from './ConnectorHelper'

const t = i18n.global.t

export function getChargerType(site: IChargingSite | null | undefined, deviceId: string): string {
  if (!site?.Chargers || !deviceId) {
    return ''
  }

  const chargingType = site.Chargers.find(c => c.DeviceId === deviceId)?.ChargerModel?.ChargingType
  return convertChargingTypeToLabel(chargingType)
}

export function getChargerRegistrationStatusLabel(chargerStatus: RegistrationStatus | null | undefined) {
  const statusLabel: Record<RegistrationStatus, string> = {
    [RegistrationStatus.Operational]: 'operational',
    [RegistrationStatus.Decommissioned]: 'decommissioned',
    [RegistrationStatus.Planned]: 'planned',
    [RegistrationStatus.Unknown]: 'unknown'
  }
  return chargerStatus !== null && chargerStatus !== undefined && statusLabel[chargerStatus] ? statusLabel[chargerStatus] : 'unknown'
}

export function getChargerRegistrationStatusIcon(chargerStatus: RegistrationStatus | null | undefined) {
  const statusImageMap: Record<RegistrationStatus, string> = {
    [RegistrationStatus.Operational]: getIcon('greenCheck'),
    [RegistrationStatus.Decommissioned]: getIcon('redForbiddenCircle'),
    [RegistrationStatus.Planned]: getIcon('yellowExpand'),
    [RegistrationStatus.Unknown]: getIcon('slateQuestionCircle')
  }
  return chargerStatus !== null && chargerStatus !== undefined ? statusImageMap[chargerStatus] || getIcon('slateQuestionCircle') : getIcon('slateQuestionCircle')
}

export function getChargerSimStatusIcon(simStatus: string | null | undefined) {
  const simStatusImageMap: Record<string, string> = {
    ['active']: getIcon('greenCheck'),
    ['inactive']: getIcon('redForbiddenCircle')
  }

  return simStatus !== null && simStatus !== undefined ? simStatusImageMap[simStatus.toLowerCase()] || getIcon('slateQuestionCircle') : getIcon('slateQuestionCircle')
}

export function getChargingTypeLabel(chargingType: ChargingType | undefined) {
  const chargingTypeMap: Record<ChargingType, ChargingTypeLabel> = {
    [ChargingType.Slow]: ChargingTypeLabel.Slow,
    [ChargingType.Fast]: ChargingTypeLabel.Fast,
    [ChargingType.UltraFast]: ChargingTypeLabel.UltraFast
  }

  return chargingType !== undefined && chargingType in chargingTypeMap ? chargingTypeMap[chargingType] : undefined
}

export function getConnectivityMethod(charger: ICharger, localController: ILocalController | null | undefined): string {
  if (!charger?.IpAddress) {
    return 'NOT CONNECTED'
  }
  return charger.IpAddress === localController?.LocalControllerConnection?.IpAddress ? 'ETHERNET' : 'SIM'
}

export function getChargerConnectivityStatusIcon(connectivityStatus: string | null | undefined) {
  const connectivityImageMap: Record<string, string> = {
    ['online']: getIcon('greenCheck'),
    ['offline']: getIcon('redForbiddenCircle')
  }

  return connectivityStatus !== null && connectivityStatus !== undefined ? connectivityImageMap[connectivityStatus.toLowerCase()] || getIcon('slateQuestionCircle') : getIcon('slateQuestionCircle')
}

export function isLoadBalanced(charger: ICharger | null | undefined, localController: ILocalController | null | undefined): boolean {
  return charger !== null && charger !== undefined && charger.IpAddress === localController?.LocalControllerConnection?.IpAddress
}

export function buildChargerList(site: IChargingSite | null | undefined): IPaginatedResponse<ICharger> | null {
  const chargerList: ICharger[] | null | undefined = site?.Chargers
  if (chargerList) {
    chargerList.forEach(ch => (ch.ChargingSite = site ?? null))
    return {
      PageIndex: 1,
      TotalPages: 1,
      TotalRecords: chargerList.length,
      Values: chargerList
    }
  }
  return null
}

export function buildConnectorStaticStatusList(flatConnectors: IFlatConnector[]): IUpdateConnectorStaticStatus {
  return {
    Connectors: flatConnectors.map(fc => ({
      EvseId: fc.EvseElementId ? parseInt(fc.EvseElementId, 10) : 0,
      ConnectorId: fc.ConnectorElementId ? parseInt(fc.ConnectorElementId, 10) : 0,
      DynamicsCpmStatus: fc.LifeCycleStatus ?? ConnectorDynamicsCpmStatus.Available
    }))
  }
}

export function convertToChargerManuallyFaulted(flatConnectors: IFlatConnector[]): IChargerManuallyFaulted {
  const connectors: IConnectorManuallyFaulted[] = flatConnectors.map(fc => ({
    EvseId: fc.EvseElementId,
    ConnectorId: fc.ConnectorElementId ? parseInt(fc.ConnectorElementId, 10) : 0,
    ManuallyFaulted: fc.ManuallyFaulted ?? false
  }))

  const manuallyFaulted = connectors.every(c => c.ManuallyFaulted === true)

  return {
    ManuallyFaulted: manuallyFaulted,
    Connectors: connectors
  }
}

export const isOcpp2 = (charger?: ICharger): boolean => !!charger && !!charger.OcppProtocol && charger.OcppProtocol.includes('2')

export const getFormatedProtocol = (charger: ICharger): string => (isOcpp2(charger) ? 'OCPP201' : 'OCPP16')

export const stateItemsChargers: IStateItemCharger[] = [
  {
    state: 'Operational',
    value: RegistrationStatus.Operational,
    label: t('chargerDetails.status.operational')
  },
  {
    state: 'Planned',
    value: RegistrationStatus.Planned,
    label: t('chargerDetails.status.planned')
  },
  {
    state: 'Decommissioned',
    value: RegistrationStatus.Decommissioned,
    label: t('chargerDetails.status.decommissioned')
  },
  {
    state: 'Unknown',
    value: RegistrationStatus.Unknown,
    label: t('chargerDetails.status.unknown')
  }
]

export const chargerAvailabilities: ChargerAvailabilityStatus[] = [ChargerAvailabilityStatus.Inoperative, ChargerAvailabilityStatus.Operative]

export function buildChargersStatusCustomCondition(connectorStatusFilterCondition: FilterCondition, statusMetaData: DataTableFilterMetaData): FilterCondition {
  connectorStatusFilterCondition.predicate = ''
  const statusValue = extractValues(statusMetaData)
  if (statusValue !== undefined && statusValue !== null) {
    connectorStatusFilterCondition.predicate = `
      Status != null &&
      Status in (${statusValue})
    `
  }
  return connectorStatusFilterCondition
}

export function getCertificateTypeLabel(certificateType: CertificateType): string {
  switch (certificateType) {
    case CertificateType.V2GRootCertificate:
      return t('chargerDetails.certificates.types.v2gRootCertificate')
    case CertificateType.MoRootCertificate:
      return t('chargerDetails.certificates.types.moRootCertificate')
    case CertificateType.CsmsRootCertificate:
      return t('chargerDetails.certificates.types.csmsRootCertificate')
    case CertificateType.V2GCertificateChain:
      return t('chargerDetails.certificates.types.v2gCertificateChain')
    case CertificateType.ManufacturerRootCertificate:
      return t('chargerDetails.certificates.types.manufacturerRootCertificate')
    case CertificateType.ChargingStationCertificate:
      return t('chargerDetails.certificates.types.chargingStationCertificate')
    default:
      return String(certificateType)
  }
}

export function getCertificateTypeSeverity(certificateType: CertificateType): ButtonSeverity {
  switch (certificateType) {
    case CertificateType.V2GRootCertificate:
      return 'success'
    case CertificateType.MoRootCertificate:
      return 'info'
    case CertificateType.CsmsRootCertificate:
      return 'warn'
    case CertificateType.V2GCertificateChain:
      return 'secondary'
    case CertificateType.ManufacturerRootCertificate:
      return 'contrast'
    case CertificateType.ChargingStationCertificate:
      return 'danger'
    default:
      return 'info'
  }
}

function getConnectorDisplayStatus(connector: IConnector): string {
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Blocked) {
    return 'blocked'
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.Removed) {
    return 'removed'
  }
  if (connector.LifeCycleStatus === ConnectorDynamicsCpmStatus.OutOfOrder) {
    return 'faulted'
  }
  return getConnectorStatusLabel(connector.ChargingStatus)
}

export function getChargePointStatus(charger: ICharger | null | undefined): string {
  if (!charger) {
    return 'unknown'
  }
  if (charger.Status === RegistrationStatus.Planned) {
    return 'planned'
  }

  const connectors = charger.Evses?.flatMap(evse => evse.Connectors ?? []) ?? []
  if (connectors.length === 0) {
    return 'unknown'
  }

  const statuses = connectors.map(getConnectorDisplayStatus)
  if (statuses.every(s => s === 'removed')) {
    return 'removed'
  }

  const nonBlocked = statuses.filter(s => s !== 'blocked')
  if (nonBlocked.length === 0) {
    return 'blocked'
  }

  if (nonBlocked.every(s => s === 'removed')) {
    return 'removed'
  }

  const priority = ['occupied', 'available', 'unavailable', 'reserved', 'faulted', 'unknown']
  for (const status of priority) {
    if (nonBlocked.includes(status)) {
      return status
    }
  }
  return 'unknown'
}

export function getChargePointStatusIcon(status: string | null | undefined) {
  const chargePointStatusImageMap: Record<string, string> = {
    ['available']: getIcon('greenCheck'),
    ['faulted']: getIcon('redMinusCircle'),
    ['occupied']: getIcon('yellowCar'),
    ['reserved']: getIcon('blueLock'),
    ['unavailable']: getIcon('stoneCrossCircle'),
    ['unknown']: getIcon('slateQuestionCircle'),
    ['blocked']: getIcon('redBlocked'),
    ['removed']: getIcon('redRemoved'),
    ['planned']: getIcon('yellowExpand')
  }

  return status !== null && status !== undefined ? chargePointStatusImageMap[status.toLowerCase()] || getIcon('slateQuestionCircle') : getIcon('slateQuestionCircle')
}

export function getOpenCases(charger: ICharger) {
  if (!charger || !charger.OpenCases) {
    return ''
  }
  let duration: string | null = ''
  if (charger.OldestActiveCaseDateTime) {
    duration = getFormattedDuration(charger.OldestActiveCaseDateTime, new Date())
  }
  if (duration) {
    return `${charger.OpenCases} - ${duration}`
  }
  return `${charger.OpenCases} - `
}
